/**
 * The drag-and-drop email builder — GrapesJS with the newsletter preset —
 * shared by the template editor and the block editor.
 *
 * Contract: `docs/api/marketing.md`, "FE shared components". The props and the
 * handle are binding; nothing else about this file is.
 *
 * ⚠️ THE PARENT DECIDES THE HEIGHT. The builder fills whatever box it is given
 * (`h-full`), and GrapesJS lays its panels out absolutely inside that box —
 * so a parent with no height gets a zero-height editor, not an auto-sized one.
 *
 * ⚠️ ONE EDITOR PER MOUNT, DESTROYED ON UNMOUNT. React 19's strict mode mounts,
 * unmounts and mounts again in development; the effect destroys what it made
 * and carries the project across, so the second mount loads exactly what the
 * first one held.
 *
 * What it exports (`getResult().html`) is a COMPLETE document: doctype,
 * viewport meta, a small email reset and the column-stacking media query in
 * `<head>`, and the body with every style inlined by the preset's
 * `gjs-get-inlined-html` command. In `mode: 'block'` it is the body FRAGMENT
 * instead, because a saved block is pasted into another email and a second
 * `<html>` inside a template is not something mail clients agree about.
 */
import 'grapesjs/dist/css/grapes.min.css';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import grapesjs, { type Component, type Editor, type ProjectData } from 'grapesjs';
import newsletterPresetModule from 'grapesjs-preset-newsletter';
import { api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { Button, EmptyState, Modal, Textarea } from '@/components/ui';
import { BUILDER_THEME_CSS } from './builder-theme';
import { COLUMN_CLASS, STARTER_HTML, bodyFragment, registerEmailBlocks, registerSavedBlocks } from './blocks';
import { groupVariables, insertVariable, registerVariableRteAction, type VariableGroup, type VariableRteAction } from './variables-plugin';
import { makeUploadFile, preloadAssets } from './assets';

export interface EmailBuilderHandle {
  getResult(): Promise<{ editorJson: unknown; html: string }>;
  loadHtml(html: string): void;
}

export interface EmailBuilderProps {
  initialProject: unknown | null;
  initialHtml: string | null;
  mode: 'template' | 'block';
  readOnly?: boolean;
  onDirtyChange?(dirty: boolean): void;
}

/*
 * The preset ships a UMD bundle whose `module.exports` is `{__esModule, default}`.
 * Vite's dev pre-bundle hands the default import over as that WHOLE OBJECT, so
 * calling it threw "newsletterPreset is not a function" inside grapesjs.init and
 * the builder never mounted. A bundler that does unwrap `__esModule` hands over
 * the function itself. Resolve both, once, here.
 */
type NewsletterPreset = typeof newsletterPresetModule;
const newsletterPreset: NewsletterPreset =
  typeof newsletterPresetModule === 'function'
    ? newsletterPresetModule
    : (newsletterPresetModule as unknown as { default: NewsletterPreset }).default;

/** The preset's command ids — its documented defaults, named once. */
const INLINE_CMD = 'gjs-get-inlined-html';
const IMPORT_CMD = 'gjs-open-import-template';

/** Below this the builder is not offered; a read-only preview is. */
const WIDE_QUERY = '(min-width: 768px)';

function subscribeWide(onChange: () => void): () => void {
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
const readWide = () => window.matchMedia(WIDE_QUERY).matches;

/** Head of every exported email: reset, plus the column stacking the blocks rely on. */
const EMAIL_HEAD_CSS =
  'body{margin:0;padding:0;width:100%!important;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}' +
  'table,td{border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;}' +
  'img{border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;}' +
  'a img{border:0;}' +
  `@media only screen and (max-width:620px){` +
  `.emerge-container{width:100%!important;max-width:100%!important;}` +
  `.${COLUMN_CLASS}{display:block!important;width:100%!important;max-width:100%!important;box-sizing:border-box;}` +
  `}`;

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

/**
 * Splits exported markup into the body's own style, its contents and any
 * `<style>` blocks (the inliner keeps media queries in one). `getHtml` exports
 * the wrapper AS `<body>`, so the body's background survives as its style.
 */
function splitExport(markup: string): { bodyStyle: string; body: string; styles: string[] } {
  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const styles = Array.from(doc.querySelectorAll('style')).map((s) => s.textContent ?? '');
  doc.querySelectorAll('style').forEach((s) => s.remove());
  return { bodyStyle: doc.body.getAttribute('style') ?? '', body: doc.body.innerHTML, styles };
}

function emailDocument(parts: { bodyStyle: string; body: string; styles: string[] }): string {
  const extra = parts.styles.filter((s) => s.trim()).join('\n');
  const bodyStyle = parts.bodyStyle ? ` style="${escapeAttr(parts.bodyStyle)}"` : ' style="margin:0;padding:0;"';
  return (
    '<!DOCTYPE html>\n' +
    '<html lang="en" xmlns="http://www.w3.org/1999/xhtml">\n<head>\n' +
    '<meta charset="utf-8">\n' +
    '<meta name="viewport" content="width=device-width, initial-scale=1">\n' +
    '<meta http-equiv="X-UA-Compatible" content="IE=edge">\n' +
    '<meta name="x-apple-disable-message-reformatting">\n' +
    '<title></title>\n' +
    `<style>${EMAIL_HEAD_CSS}${extra ? `\n${extra}` : ''}</style>\n` +
    `</head>\n<body${bodyStyle}>\n${parts.body}\n</body>\n</html>\n`
  );
}

/** Inlined when the preset's command is there; `<style>`-in-head when it is not. */
function exportMarkup(editor: Editor): string {
  let inlined: unknown;
  try {
    inlined = editor.Commands.has(INLINE_CMD) ? editor.runCommand(INLINE_CMD) : undefined;
  } catch {
    inlined = undefined;
  }
  if (typeof inlined === 'string' && inlined.trim()) return inlined;
  return `${editor.getHtml()}<style>${editor.getCss() ?? ''}</style>`;
}

function exportHtml(editor: Editor, mode: EmailBuilderProps['mode']): string {
  const parts = splitExport(exportMarkup(editor));
  if (mode === 'block') {
    const style = parts.styles.filter((s) => s.trim()).map((s) => `<style>${s}</style>`).join('');
    return style + parts.body;
  }
  return emailDocument(parts);
}

/** A project JSON GrapesJS can load: an object, with pages. */
function isProject(value: unknown): value is ProjectData {
  return typeof value === 'object' && value !== null && Array.isArray((value as { pages?: unknown }).pages);
}

/** Nothing in the canvas may be selected, moved, edited or removed. */
function lockComponents(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) return;
  wrapper.set({ droppable: false, selectable: false, hoverable: false });
  wrapper.components().forEach((child: Component) =>
    child.onAll((cmp: Component) =>
      cmp.set({
        editable: false,
        draggable: false,
        droppable: false,
        selectable: false,
        hoverable: false,
        removable: false,
        copyable: false,
        badgable: false,
        highlightable: false,
      }),
    ),
  );
}

type VariablesState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'ready'; groups: VariableGroup[] };

export const EmailBuilder = forwardRef<EmailBuilderHandle, EmailBuilderProps>(function EmailBuilder(
  { initialProject, initialHtml, mode, readOnly = false, onDirtyChange },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);

  /*
   * Read through refs, so a parent passing a fresh callback or a new initial
   * value each render neither re-creates the editor nor reloads its content.
   * The initial values are read ONCE, at the first mount; after that the
   * project carried across a re-mount is the editor's own.
   */
  const onDirtyRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyRef.current = onDirtyChange;
  }, [onDirtyChange]);
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  const carriedRef = useRef<{ project: unknown | null; html: string | null }>({
    project: initialProject,
    html: initialHtml,
  });
  /** `loadHtml` called before the editor exists — applied when it does. */
  const pendingHtmlRef = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  const loadedRef = useRef(false);

  const [status, setStatus] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const openImportRef = useRef(() => setImportOpen(true));

  const wide = useSyncExternalStore(subscribeWide, readWide, () => true);

  const setDirty = useCallback((next: boolean) => {
    if (dirtyRef.current === next) return;
    dirtyRef.current = next;
    onDirtyRef.current?.(next);
  }, []);

  /* --- the editor itself ------------------------------------------------- */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const instance = grapesjs.init({
      container,
      height: '100%',
      width: 'auto',
      fromElement: false,
      telemetry: false,
      storageManager: false,
      // Inlined onto the body only; GrapesJS's default box-sizing-on-everything
      // would be inlined onto every element of every email.
      protectedCss: 'body{margin:0;padding:0;}',
      assetManager: {
        upload: false,
        uploadFile: makeUploadFile(() => editorRef.current, setStatus),
        showUrlInput: false,
        autoAdd: true,
      },
      plugins: [
        (ed: Editor) =>
          newsletterPreset(ed, {
            blocks: [],
            useCustomTheme: false,
            showBlocksOnLoad: !readOnly,
            showStylesOnChange: true,
            updateStyleManager: true,
            inlineCss: true,
          }),
      ],
    });
    editorRef.current = instance;

    registerEmailBlocks(instance);

    // The preset's import button opens OUR modal, which goes through loadHtml.
    instance.Commands.add(IMPORT_CMD, (_ed: Editor, sender: { set?: (key: string, value: unknown) => void } | undefined) => {
      sender?.set?.('active', false);
      openImportRef.current();
    });

    const carried = carriedRef.current;
    const pending = pendingHtmlRef.current;
    pendingHtmlRef.current = null;
    let loaded = false;
    if (pending !== null) {
      instance.setComponents(bodyFragment(pending));
      loaded = true;
    } else if (isProject(carried.project)) {
      try {
        instance.loadProjectData(carried.project);
        loaded = true;
      } catch {
        loaded = false;
      }
    }
    if (!loaded && carried.html) {
      instance.setComponents(bodyFragment(carried.html));
      loaded = true;
    }
    if (!loaded && modeRef.current === 'template' && !readOnly) {
      instance.setComponents(STARTER_HTML);
    }
    if (readOnly) lockComponents(instance);

    loadedRef.current = false;
    const settle = () => {
      instance.UndoManager.clear();
      instance.clearDirtyCount();
      loadedRef.current = true;
    };
    instance.onReady(settle);

    const onUpdate = () => {
      if (!loadedRef.current) return;
      if (instance.getDirtyCount() > 0) setDirty(true);
    };
    instance.on('update', onUpdate);

    setEditor(instance);

    return () => {
      instance.off('update', onUpdate);
      try {
        carriedRef.current = { project: instance.getProjectData(), html: null };
      } catch {
        // Keep what was carried before; a failed read is not a reason to lose it.
      }
      editorRef.current = null;
      loadedRef.current = false;
      instance.destroy();
      setEditor(null);
    };
  }, [readOnly, setDirty]);

  /* --- variables: the panel and the rich-text dropdown ------------------- */
  const [variables, setVariables] = useState<VariablesState>({ kind: 'loading' });
  useEffect(() => {
    const controller = new AbortController();
    api
      .listMarketingVariables(controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setVariables({ kind: 'ready', groups: groupVariables(res.data.builtIn, res.data.custom) });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setVariables({ kind: 'failed' });
      });
    return () => controller.abort();
  }, []);

  const rteActionRef = useRef<VariableRteAction | null>(null);
  useEffect(() => {
    if (!editor || readOnly) return;
    rteActionRef.current = registerVariableRteAction(editor);
    return () => {
      rteActionRef.current = null;
    };
  }, [editor, readOnly]);
  useEffect(() => {
    if (variables.kind === 'ready') rteActionRef.current?.setGroups(variables.groups);
  }, [variables, editor]);

  /* --- saved blocks and uploaded images ---------------------------------- */
  useEffect(() => {
    if (!editor || readOnly) return;
    const controller = new AbortController();
    api
      .listMarketingBlocks({ archived: false }, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        registerSavedBlocks(editor, res.data);
      })
      .catch(() => {
        // The house blocks are still there; a missing saved set is not a broken builder.
      });
    preloadAssets(editor, controller.signal).catch(() => {
      // Uploads still work; the gallery is simply empty until one is made.
    });
    return () => controller.abort();
  }, [editor, readOnly]);

  /* --- coming back to a wide screen: re-measure the canvas ---------------- */
  useEffect(() => {
    if (wide) editor?.refresh();
  }, [wide, editor]);

  /* --- the handle ---------------------------------------------------------- */
  useImperativeHandle(
    ref,
    () => ({
      getResult: async () => {
        const current = editorRef.current;
        if (!current) {
          const carried = carriedRef.current;
          return { editorJson: carried.project, html: pendingHtmlRef.current ?? carried.html ?? '' };
        }
        const result = { editorJson: current.getProjectData() as unknown, html: exportHtml(current, modeRef.current) };
        current.clearDirtyCount();
        setDirty(false);
        return result;
      },
      loadHtml: (html: string) => {
        const current = editorRef.current;
        if (!current) {
          pendingHtmlRef.current = html;
          return;
        }
        current.CssComposer.clear();
        current.setComponents(bodyFragment(html));
        if (readOnly) lockComponents(current);
        else setDirty(true);
      },
    }),
    [readOnly, setDirty],
  );

  /* --- import HTML --------------------------------------------------------- */
  const [importText, setImportText] = useState('');
  const confirmImport = () => {
    const current = editorRef.current;
    if (!current || !importText.trim()) return;
    current.CssComposer.clear();
    current.setComponents(bodyFragment(importText));
    setDirty(true);
    setImportOpen(false);
    setImportText('');
    setStatus('HTML imported.');
  };

  /* --- the variables popover ---------------------------------------------- */
  const [variablesOpen, setVariablesOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!variablesOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVariablesOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setVariablesOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [variablesOpen]);

  const pickVariable = async (key: string) => {
    const outcome = await insertVariable(editorRef.current, key);
    const token = `{{${key}}}`;
    setStatus(
      outcome === 'inserted'
        ? `Inserted ${token}.`
        : outcome === 'copied'
          ? `Copied ${token}. Paste it into a text block.`
          : `${token} could not be inserted or copied.`,
    );
    if (outcome !== 'failed') setVariablesOpen(false);
  };

  /* --- narrow screens: a preview, never the editor ------------------------- */
  const previewHtml = useMemo(() => {
    if (wide) return '';
    if (editor) {
      try {
        return exportHtml(editor, 'template');
      } catch {
        return initialHtml ?? '';
      }
    }
    return initialHtml ?? '';
  }, [wide, editor, initialHtml]);

  return (
    <div className={cx('emerge-builder relative isolate flex h-full min-h-0 flex-col', readOnly && 'emerge-builder--readonly')}>
      <style>{BUILDER_THEME_CSS}</style>

      {!wide && (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto">
          <EmptyState
            title="The email builder needs a wider screen"
            description="Open this on a screen at least 768px wide to edit it. A read-only preview is below."
          />
          {previewHtml ? (
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={previewHtml}
              className="h-[480px] w-full shrink-0 rounded-md border border-border bg-surface"
            />
          ) : (
            <p className="px-4 pb-4 text-center text-xs text-fg-muted">There is nothing to preview yet.</p>
          )}
        </div>
      )}

      <div className={cx('flex min-h-0 flex-1 flex-col', !wide && 'hidden')}>
        {!readOnly && (
          <div className="relative flex shrink-0 flex-wrap items-center gap-2 border-b border-border bg-surface px-2 py-1.5">
            <div ref={popoverRef} className="relative">
              <Button
                size="sm"
                variant="secondary"
                aria-expanded={variablesOpen}
                aria-haspopup="dialog"
                onClick={() => setVariablesOpen((v) => !v)}
              >
                Variables
              </Button>
              {variablesOpen && (
                <div
                  role="dialog"
                  aria-label="Insert a variable"
                  className="absolute left-0 top-full z-20 mt-1 max-h-[60vh] w-80 overflow-auto rounded-lg border border-border bg-surface-raised p-1 shadow-lg"
                >
                  {variables.kind === 'loading' && <p className="px-3 py-2 text-xs text-fg-muted">Loading variables…</p>}
                  {variables.kind === 'failed' && (
                    <p className="px-3 py-2 text-xs text-danger">Variables could not be loaded.</p>
                  )}
                  {variables.kind === 'ready' && variables.groups.length === 0 && (
                    <p className="px-3 py-2 text-xs text-fg-muted">No variables are defined.</p>
                  )}
                  {variables.kind === 'ready' &&
                    variables.groups.map((group) => (
                      <div key={group.label} className="py-1">
                        <p className="px-3 pb-1 pt-1.5 text-xs font-semibold text-fg-subtle">
                          {group.label}
                        </p>
                        {group.items.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => void pickVariable(item.key)}
                            className="flex w-full flex-col items-start rounded-md px-3 py-1.5 text-left transition-colors duration-150 ease-out hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none motion-reduce:transition-none"
                          >
                            <span className="text-sm text-fg">{item.label}</span>
                            <span className="font-mono text-2xs text-fg-muted">{`{{${item.key}}}`}</span>
                          </button>
                        ))}
                      </div>
                    ))}
                </div>
              )}
            </div>
            <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
              Import HTML
            </Button>
            <p aria-live="polite" className="min-w-0 flex-1 truncate text-xs text-fg-muted" title={status ?? undefined}>
              {status}
            </p>
          </div>
        )}
        <div ref={containerRef} className="relative isolate min-h-0 flex-1" />
      </div>

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import HTML"
        size="lg"
        dismissOnBackdrop={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setImportOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmImport} disabled={!importText.trim()}>
              Replace content
            </Button>
          </>
        }
      >
        <Textarea
          aria-label="HTML to import"
          rows={14}
          value={importText}
          onChange={(e) => setImportText(e.target.value)}
          className="font-mono text-xs"
          spellCheck={false}
        />
      </Modal>
    </div>
  );
});
